// // modules/product/resolvers/product.resolver.ts
import { inject } from '@angular/core';
import {
  ResolveFn,
  RedirectCommand,
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot
} from '@angular/router';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, switchMap, take, tap } from 'rxjs/operators';
import { PanelService } from '../services/api/panel.service';
import { ProductV2, RelatedProductV2 } from '../models/products-v2.model';
import { VarietyAvailability } from '../models/payment.model';

import { FAQModels, FAQResponse } from '../models/faq.model';
import { PaginatedResponse } from '../models/pagination.model';
import { Url_Friendly_String } from '../helpers/helper';
import { PriceSuggestionsData } from '../../product/models/price-suggestions-data.model';
import { Exchange } from '../models/exchange.model';
import { CurrencyDetail } from '../models/products.model';
import { ProductComment } from '../models/comment.model';
import { AppStateService } from '../services/app-state.service';
import { ExchangeResolverService } from '../services/resolver/exchange-resolver.service';
import { StoreDetailService } from '../services/resolver/store-detail.service';
import { PriceSuggestionsService } from '../../product/services/price-suggestions.service';

export interface ProductDetailResolvedData {
  product: ProductV2;
  stockAvailability: VarietyAvailability[];
  loyaltyContent: any;
  // faq: any;
  relatedProducts: RelatedProductV2[];
  priceSuggestionsData: PriceSuggestionsData;
  productComments: ProductComment[];
}

export const productResolver: ResolveFn<ProductDetailResolvedData | RedirectCommand> = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const panelService = inject(PanelService);
  const appStateService = inject(AppStateService)
  const exchangeResolver = inject(ExchangeResolverService);
  const storeService = inject(StoreDetailService);
  const priceSuggestionsService = inject(PriceSuggestionsService);

  const id = route.paramMap.get('id');
  const nameFromUrl = route.paramMap.get('name');
  const fillQueryParam = route.queryParamMap.get('fill');

  if (!id) {
    return of(new RedirectCommand(router.parseUrl('/not-found'), { replaceUrl: true }));
  }
//http://localhost:4200/product/8458/Nintendo-eShop-FI
  return panelService.getProductDetailV2(Number(id)).pipe(
    switchMap((product: ProductV2): Observable<ProductDetailResolvedData | RedirectCommand> => {
      const correctName = Url_Friendly_String(product.product_detail.name);
      if (!nameFromUrl || nameFromUrl !== correctName) {
        return of(
          new RedirectCommand(
            router.createUrlTree(['/product', id, correctName], {
              queryParams: route.queryParams
            }),
            { replaceUrl: true }
          )
        );
      }

      // Extract SKUs from product varieties for stock availability
      const varietySKUs = product?.product_detail?.varieties?.map(
        (variety) => variety?.variety_detail?.sku
      )|| [];
      // Get store product ID for loyalty content
      const storeProductId = product?.product_detail.store_product_id;

      // Prepare parallel requests for stock availability, loyalty content, FAQ, and related products
      // Each request has individual error handling to prevent forkJoin from failing entirely
      const stockAvailability$ = varietySKUs.length > 0
        ? panelService.checkStockAvailability(varietySKUs).pipe(
            catchError(() => of([] as VarietyAvailability[]))
          )
        : of([] as VarietyAvailability[]);

      // Two-phase render: resolver always returns guest-safe data.
      // Club-specific loyalty content is fetched on the client after profile hydration.
      const loyaltyContent$ = storeProductId ? panelService.getLoyaltyContent(storeProductId).pipe(
        catchError(() => of(null as any))
      ) : of(null as any);

      // const faq$ = panelService.getFAQDetailByModel(FAQModels.storeproduct, product.product_detail.store_product_id).pipe(
      //   catchError(() => of(null as FAQResponse))
      // );
      const relatedProducts$ = storeProductId
        ? panelService.getRelatedProducts(storeProductId).pipe(
            catchError(() => of([] as RelatedProductV2[]))
          )
        : of([] as RelatedProductV2[]);
      // Exchange rates must be loaded before price math; permissionInit can emit [] on first tick.
      const exchangeRates$ = (() => {
        const cached = appStateService.getExchangeDetail();
        if (cached?.length > 0) {
          return of(cached as Exchange[]);
        }
        return panelService.getExchangeDetail().pipe(
          tap((rates) => {
            if (rates?.length) {
              appStateService.setExchangeDetail(rates);
              exchangeResolver.permissionInit = rates;
            }
          }),
          catchError(() => of([] as Exchange[]))
        );
      })();

      const productComments$ = panelService.getProductComments(id).pipe(
        catchError(() => of([] as ProductComment[]))
      );

      // Get store currency - try AppState first, then service
      const storeCurrency$ = (() => {
        const cachedStore = appStateService.getStoreDetail();
        if (cachedStore?.currency_detail) {
          return of(cachedStore.currency_detail as CurrencyDetail);
        }
        const storeDetail = storeService.permissionInit;
        if (storeDetail?.currency_detail) {
          return of(storeDetail.currency_detail as CurrencyDetail);
        }
        // Fallback to product currency if store currency not available
        // Convert Omit<CurrencyDetail, 'name'> to CurrencyDetail by adding name
        return of({
          ...product.currency_detail,
          name: product.currency_detail.code // Use code as fallback name
        } as CurrencyDetail);
      })();

      return forkJoin({
        stockAvailability: stockAvailability$,
        loyaltyContent: loyaltyContent$,
        // faq: faq$,
        relatedProducts: relatedProducts$,
        exchangeRates: exchangeRates$,
        storeCurrency: storeCurrency$,
        productComments: productComments$
      }).pipe(
        switchMap(({ stockAvailability, loyaltyContent, relatedProducts, exchangeRates, storeCurrency, productComments }) => {
          // Extract FAQResponse from paginated results (matching FAQ component logic)
          // const faqData = faq;

          // Calculate price suggestions BEFORE returning
          // This ensures SSR gets fully calculated data
          let priceSuggestionsData: PriceSuggestionsData;
          
          try {
            // Ensure storeCurrency has name property
            const fullStoreCurrency: CurrencyDetail = storeCurrency || {
              ...product.currency_detail,
              name: product.currency_detail.code
            } as CurrencyDetail;

            priceSuggestionsData = priceSuggestionsService.calculatePriceSuggestions({
              product,
              stockAvailability: stockAvailability || [],
              exchangeRates: exchangeRates || [],
              storeCurrency: fullStoreCurrency,
              fillQueryParam: fillQueryParam ? +fillQueryParam : null
            } as {
              product: ProductV2;
              stockAvailability: VarietyAvailability[];
              exchangeRates: Exchange[];
              storeCurrency: CurrencyDetail;
              fillQueryParam: number | null;
            });
          } catch (error) {
            // console.error('Error calculating price suggestions:', error);
            // Return fallback data
            const fallbackCurrency = {
              ...product.currency_detail,
              name: product.currency_detail.code
            } as CurrencyDetail;
            priceSuggestionsData = {
              pricingType: 'FIXED' as any,
              exchange: {
                rate: 1,
                fromCurrency: product.currency_detail,
                toCurrency: product.currency_detail,
                hasExchange: false
              },
              defaultSelection: {
                type: 'FIXED',
                price: 0,
                sku: '',
                isValid: false
              },
              stockStatus: {
                isFullyOutOfStock: true,
                hasSomeInStock: false
              },
              processedVarieties: []
            };
          }
          return of({
            product,
            stockAvailability: stockAvailability || [],
            loyaltyContent: loyaltyContent || null,
            // faq: faqData,
            relatedProducts: relatedProducts || [],
            priceSuggestionsData,
            productComments: productComments || []
          });
        })
      );
    }),
    catchError(() => {
      return of(new RedirectCommand(router.parseUrl('/not-found'), { replaceUrl: true }));
    })
  );
};